/**
 * Recently-Viewed Utilities
 *
 * Single source of truth for the Homepage "recently viewed" logic used by the
 * DynamicTabbedLayout (DTL) slot pipeline. Resolves recently-viewed images from
 * client localStorage, detects the recently-viewed authoring cue, and hydrates
 * Mango (Riott) primary-image assets from the stored recently-viewed image when
 * the Target payload arrives without imagery.
 */
import { logger } from '@marriott/shared/mi-helper-utils';
import { isRecord } from './helper';
import {
  RECENTLY_VIEWED_STORAGE_KEY,
  RETARGETING_RECENTLY_VIEWED_CANDIDATES_INVALID_EVENT,
} from './retargeting/retargeting.constants';

const { log } = logger({})('recentlyViewedUtils');

/** Mango UXL assets must include at least one non-empty primary image URL (matches RiottMediaCard / helpers). */
const MANGO_PRIMARY_IMAGE_KEYS = ['primaryImage_desktop', 'primaryImage_mobile', 'primaryImage_tablet'] as const;

const CACHE_IMAGE_DOMAIN = 'https://cache.marriott.com';

export type RecentlyViewedImageVariant =
  | 'square'
  | 'rectangle'
  | 'standardHorizontal'
  | 'panoramicHorizontal'
  | 'classicVertical';

const RECENTLY_VIEWED_RENDITION_BY_VARIANT: Record<RecentlyViewedImageVariant, string> = {
  square: 'Square',
  rectangle: 'Wide-Hor',
  standardHorizontal: 'Standard-Hor',
  panoramicHorizontal: 'Pano-Hor',
  classicVertical: 'Classic-Ver',
};

export function hasNonEmptyMangoPrimaryImageFields(assets: Record<string, unknown>): boolean {
  for (const key of MANGO_PRIMARY_IMAGE_KEYS) {
    const v = assets[key];
    if (typeof v === 'string' && v.trim() !== '') {
      return true;
    }
  }
  return false;
}

function toAbsoluteCacheUrl(pathOrUrl: string): string {
  const value = (pathOrUrl || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return value.startsWith('/') ? `${CACHE_IMAGE_DOMAIN}${value}` : value;
}

function toRecentlyViewedVariantUrl(pathOrUrl: string, variant: RecentlyViewedImageVariant): string {
  const absolute = toAbsoluteCacheUrl(pathOrUrl);
  if (!absolute) return '';

  const base = absolute.split('?')[0] ?? '';
  if (!base.includes('/is/image/')) {
    return absolute;
  }

  const lastSlash = base.lastIndexOf('/');
  const lastColon = base.lastIndexOf(':');
  const rendition = RECENTLY_VIEWED_RENDITION_BY_VARIANT[variant];
  const variantBase = lastColon > lastSlash ? `${base.slice(0, lastColon)}:${rendition}` : `${base}:${rendition}`;

  return variantBase;
}

export function getRecentlyViewedImageFromLocalStorage(variant: RecentlyViewedImageVariant): string {
  if (typeof window === 'undefined') return '';

  try {
    const stored = window.localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY);
    if (!stored) return '';

    const parsed: unknown = JSON.parse(stored);
    if (!isRecord(parsed)) return '';
    const entries = parsed['recentlyViewedProperties'];
    if (!Array.isArray(entries)) return '';

    const validEntryCount = entries.filter(entry => {
      if (!isRecord(entry)) return false;
      const imageSrc = entry['imageSrc'];
      const imageUrl = entry['imageUrl'];
      return (
        (typeof imageSrc === 'string' && imageSrc.trim().length > 0) ||
        (typeof imageUrl === 'string' && imageUrl.trim().length > 0)
      );
    }).length;
    const rejectedCandidateCount = entries.length - validEntryCount;
    if (rejectedCandidateCount > 0) {
      log.warn(RETARGETING_RECENTLY_VIEWED_CANDIDATES_INVALID_EVENT, {
        storageKey: RECENTLY_VIEWED_STORAGE_KEY,
        totalCandidateCount: entries.length,
        rejectedCandidateCount,
      });
    }

    const rv = entries[0];
    if (!isRecord(rv)) return '';
    const imageSrc = typeof rv['imageSrc'] === 'string' ? rv['imageSrc'] : '';
    const imageUrl = typeof rv['imageUrl'] === 'string' ? rv['imageUrl'] : '';

    const candidate = imageSrc && imageSrc.indexOf('.tif') === -1 ? imageSrc : imageUrl;
    if (!candidate) return '';
    return toRecentlyViewedVariantUrl(candidate, variant);
  } catch {
    return '';
  }
}

export function hasRecentlyViewedMetaCue(metaData: Record<string, unknown> | undefined): boolean {
  if (!metaData) return false;

  if (metaData['isRecentlyViewed'] === true || metaData['isRecentlyViewed'] === 'true') {
    return true;
  }
  return false;
}

export function getRecentlyViewedImageFallbackForPayload(
  payload: Record<string, unknown>,
  variant: RecentlyViewedImageVariant
): string {
  const rawMeta = payload['metaData'] ?? payload['metadata'];
  const metaData = isRecord(rawMeta) ? rawMeta : undefined;
  if (!hasRecentlyViewedMetaCue(metaData)) return '';

  return getRecentlyViewedImageFromLocalStorage(variant);
}

export function hydrateRecentlyViewedMangoPrimaryImages(payload: Record<string, unknown>): boolean {
  const rawMeta = payload['metaData'] ?? payload['metadata'];
  const metaData = isRecord(rawMeta) ? rawMeta : undefined;
  if (!hasRecentlyViewedMetaCue(metaData)) return false;

  const adData = payload['adData'];
  const firstAd = Array.isArray(adData) ? adData[0] : undefined;
  if (!isRecord(firstAd)) return false;

  let assetsRecord: Record<string, unknown>;
  if (isRecord(firstAd['assets'])) {
    assetsRecord = firstAd['assets'];
  } else {
    assetsRecord = {};
    firstAd['assets'] = assetsRecord;
  }

  if (hasNonEmptyMangoPrimaryImageFields(assetsRecord)) {
    return true;
  }

  const squareImageUrl = getRecentlyViewedImageFromLocalStorage('square');
  if (!squareImageUrl) return false;

  assetsRecord['primaryImage_desktop'] = squareImageUrl;
  assetsRecord['primaryImage_tablet'] = squareImageUrl;
  assetsRecord['primaryImage_mobile'] = squareImageUrl;
  return true;
}
